/**
 * ScoreDisplay component
 *
 * Shows real-time score, combo counter, and last hit rating.
 * Warm palette with amber, sage, and coral accents.
 */

import React, { useEffect, useRef } from 'react';

interface ScoreDisplayProps {
  score: number;
  combo: number;
  maxCombo: number;
  lastRating: 'PERFECT' | 'GOOD' | 'MISS' | null;
}

const RATING_CONFIG = {
  PERFECT: { color: '#81b29a', label: 'PERFECT' }, // sage
  GOOD:    { color: '#e8b86d', label: 'GOOD' },    // gold
  MISS:    { color: '#e07a5f', label: 'MISS' },    // coral
} as const;

/** Animate a number counter from previous to next value. */
function useCounterAnimation(value: number): number {
  const displayRef = useRef(value);
  const [display, setDisplay] = React.useState(value);

  useEffect(() => {
    const start = displayRef.current;
    const end = value;
    if (start === end) return;

    const steps = 8;
    const step = (end - start) / steps;
    let current = start;
    let count = 0;

    const interval = setInterval(() => {
      count++;
      current += step;
      if (count >= steps) {
        setDisplay(end);
        displayRef.current = end;
        clearInterval(interval);
      } else {
        setDisplay(Math.round(current));
      }
    }, 16);

    return () => clearInterval(interval);
  }, [value]);

  return display;
}

export const ScoreDisplay: React.FC<ScoreDisplayProps> = ({
  score,
  combo,
  lastRating,
}) => {
  const animatedScore = useCounterAnimation(score);
  const ratingConfig = lastRating ? RATING_CONFIG[lastRating] : null;

  return (
    <div className="flex flex-col items-end gap-3 select-none">
      {/* Score */}
      <div className="text-right">
        <p className="text-stone-500 text-xs uppercase tracking-widest mb-1">Score</p>
        <p className="text-3xl font-bold font-mono tabular-nums text-amber-400">
          {animatedScore.toLocaleString()}
        </p>
      </div>

      {/* Combo */}
      {combo > 0 && (
        <div className="text-right">
          <p className="text-stone-500 text-xs uppercase tracking-widest mb-1">
            Combo
          </p>
          <p
            className="text-2xl font-bold font-mono"
            style={{
              color: combo >= 50 ? '#e07a5f' : combo >= 20 ? '#e8b86d' : '#e7e5e4',
            }}
          >
            ×{combo}
          </p>
        </div>
      )}

      {/* Rating flash */}
      {ratingConfig && (
        <div
          key={`${lastRating}-${score}`}
          className="text-right animate-fade-in"
        >
          <p
            className="text-lg font-bold tracking-wider"
            style={{ color: ratingConfig.color }}
          >
            {ratingConfig.label}
          </p>
        </div>
      )}
    </div>
  );
};
